import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { BaseScalableView } from "by/gamefactory/cityversetycoon/scenes/common/BaseScalableView";
import { GameFont } from "by/gamefactory/cityversetycoon/scenes/common/GameFont";
import { ButtonWithScaleAnimation } from "by/gamefactory/cityversetycoon/scenes/common/components/ButtonWithScaleAnimation";
import { UIScrollBox } from "by/gamefactory/cityversetycoon/scenes/common/components/UIScrollBox";
import { BenefitItem } from "by/gamefactory/cityversetycoon/scenes/common/models/benefits/BenefitItem";
import { IBenefitsModel } from "by/gamefactory/cityversetycoon/scenes/common/models/benefits/IBenefitsModel";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { FadeScreen } from "by/gamefactory/cityversetycoon/scenes/game/components/FadeScreen";
import { IFocusable } from "by/gamefactory/cityversetycoon/scenes/layers/IFocusable";
import { IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { DiscountCouponContainer } from "../components/DiscountCouponContainer";
import { DiscountCouponState } from "../components/DiscountCouponState";
import { DiscountResourcePackage } from "../resources/DiscountResourcePackage";
import { DynamicResourcesPackage } from "resources/DynamicResourcesPackage";
import { CancellationToken } from "async/CancellationToken";


export class MyDiscountPopupView extends BaseScalableView<void> implements IFocusable {
    public readonly focusable: boolean = true;

    private readonly cancellationToken: CancellationToken;
    private readonly commonResources: CommonResourcesPackage;
    private readonly benefitsDynamicResources: DynamicResourcesPackage;
    private readonly discountResources: DiscountResourcePackage;
    private readonly benefitModel: IBenefitsModel;

    private fadeScreen: FadeScreen;
    private topLight: Sprite;
    private bottomLight:Sprite;
    private headerIcon: Sprite;
    private backButton: ButtonWithScaleAnimation;
    private headerText: Text;
    private scrollBox: UIScrollBox;
    private leftLight: Sprite;
    private rightLight: Sprite;
    private containers: Array<DiscountCouponContainer>;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        cancellationToken: CancellationToken,
        commonResources: CommonResourcesPackage,
        benefitsDynamicResources: DynamicResourcesPackage,
        discountResources: DiscountResourcePackage,
        canvasScaler: ICanvasScaler,
        benefitModel: IBenefitsModel
    ) {
        super(operationControls, canvasScaler);
        this.cancellationToken = cancellationToken;
        this.commonResources = commonResources;
        this.benefitsDynamicResources = benefitsDynamicResources;
        this.discountResources = discountResources;
        this.benefitModel = benefitModel;
        this.containers = [];
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.cancellationToken.onCanceled.add(this.onCancellationRequested, this);
    }

    public async start(): Promise<void> {
        await this.show();
    }

    private build(): void {
        const width = this.canvasScaler.referenceWidth;
        const height = this.canvasScaler.referenceHeight;
        this.fadeScreen = new FadeScreen(0.1, 0, 0.7, width, height);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);
        this.topLight = new Sprite(this.discountResources.assets_views_discount_discount_top_light_png);
        this.addChild(this.topLight);
        this.bottomLight = new Sprite(this.discountResources.assets_views_discount_bottom_light_png);
        this.bottomLight.pivot.y = this.bottomLight.height;
        this.addChild(this.bottomLight);
        this.headerIcon = new Sprite(this.discountResources.assets_views_discount_discount_text_png);
        this.addChild(this.headerIcon);
        this.backButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_back_png);
        this.backButton.width = 35;
        this.backButton.height = 56;
        this.backButton.hitArea = new Rectangle(-this.backButton.width * 0.5, -this.backButton.height, this.backButton.width * 2, this.backButton.height * 2);
        this.backButton.onUp.add(this.backButtonClickHandle, this);
        this.addChild(this.backButton);
        this.headerText = new Text(`YOUR VALID DISCOUNT COUNPONS:`, new HeaderTextStyle());
        this.addChild(this.headerText);
        this.leftLight = new Sprite(this.discountResources.assets_views_discount_blue_light_png);
        this.leftLight.scale.x *= -1;
        this.leftLight.pivot.x = this.leftLight.width;
        this.addChild(this.leftLight);
        this.rightLight = new Sprite(this.discountResources.assets_views_discount_blue_light_png);
        this.rightLight.pivot.x = this.rightLight.width;
        this.addChild(this.rightLight);
        this.scrollBox = new UIScrollBox({
            width: width,
            height: 1203,
            type: "vertical",
            elementsMargin: -80
        });
        this.addChild(this.scrollBox);
        this.benefitModel.benefits.value?.forEach(item => this.discountContainerFactory(item));
        if (this.benefitModel.benefits.value.length < 5) {
            this.scrollBox.setScrollable(false);
            this.scrollBox.setWheelScrollable(false);
        }
    }

    private async show(): Promise<void> {
        await this.fadeScreen.fadeIn();
    }

    private layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.bottomLight.position.set(0,height);
        this.headerIcon.position.set(27, 41);
        this.backButton.position.set(55, 306);
        this.leftLight.position.set(0, 305);
        this.rightLight.position.set(width, 305);
        if (this.scrollBox.items.length > 3) {
            this.scrollBox.position.set(0, 589);
        } else {
            this.scrollBox.position.set(0, 801);
        }
        this.headerText.position.set(105, this.scrollBox.y - 68 - this.headerText.height);
        this.layoutContainers();
    }

    private onCancellationRequested(): void {
        this.operationControls.complete();
        this.destroy();
    }

    private async backButtonClickHandle(): Promise<void> {
        await this.fadeScreen.fadeOut();
        this.operationControls.complete();
    }

    private discountContainerFactory(benefitItem: BenefitItem): DiscountCouponContainer {
        const container = new DiscountCouponContainer(this.benefitsDynamicResources, this.discountResources, DiscountCouponState.UNUSED, benefitItem);
        this.containers.push(container);
        this.scrollBox.addItem(container);
        return container;
    }

    private layoutContainers(): void {
        for (let i = 0; i < this.containers.length; i++) {
            const container = this.containers[i];
            container.position.x = this.scrollBox.width * 0.5 - container.width * 0.5;
        }
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.cancellationToken.onCanceled.remove(this.onCancellationRequested, this);
        this.backButton.onUp.remove(this.backButtonClickHandle, this);
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 64
        }));
    }
}
